/**
 * <featured-products-picker>
 * ---------------------------------------------------------------------------
 * Controller for the "Featured Products" section. Where a product's colour options are modelled
 * as SEPARATE products linked via the `custom.product_group` metafield, the section pre-renders
 * every product in the group as a hidden pane (gallery + info). This element:
 *
 *   1. PREVIEW — clicking a swatch swaps the visible pane (gallery/title), no nav, no fetch.
 *   2. SELECT  — an "Add to set" toggle (which also carries the save-a-set upsell) adds/removes the
 *      previewed product from a multi-product "set"; selected products are marked on the swatch row.
 *   3. DISCOUNT — the Add-to-cart button shows the live quantity-discount (subtotal → discounted).
 *      Tiers come from the Hy "Shop Discount" scale-price config, read server-side in Liquid and
 *      passed via data-discount-tiers (authoritative; matches checkout). `amount` is the per-unit
 *      saving at that tier; total saving = amount × count.
 *   4. ADD — one /cart/add.js request commits the set (or the previewed product when none are
 *      selected), then opens the cart drawer.
 */
(function () {
  if (window.customElements.get('featured-products-picker')) return;

  // Mirror of the theme's money formatter (assets/lp-pricing.js). Self-contained because the
  // host pages use the default theme layout, not the landing-page importmap that exports it.
  function formatMoney(cents, format) {
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const sub = parts[1] ? decimal + parts[1] : '';
      return dollars + sub;
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '').match(placeholderRegex);
    if (!match) return format;
    let value = '';
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2, ',', '.'); break;
    }
    return format.replace(placeholderRegex, value);
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class FeaturedProductsPicker extends HTMLElement {
    connectedCallback() {
      // When the "build a set" flag is off, only the CORE behaviour runs (in-place colour preview
      // swap + image preload). All multi-select/upsell/discount/multi-add logic is gated below.
      this.setEnabled = this.getAttribute('data-set-enabled') === 'true';
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.mainHandle = this.getAttribute('data-main-handle');
      this.panes = Array.from(this.querySelectorAll('[data-color-pane]'));
      this.selected = new Set();

      // CORE (both modes): swatches sit after the title/rating; clicks preview-swap; preload images.
      this.panes.forEach((pane) => this.relocateSwatches(pane));
      if (!this.setEnabled) {
        // No set bar in this mode — move each pane's standard add-to-cart up to sit right after the
        // swatches (above the description), so it's reachable without scrolling past the copy.
        this.panes.forEach((pane) => this.relocateBuyButtons(pane));
      }
      this.addEventListener('click', this.onClick.bind(this));
      this.setupImagePreload();
      this.initTracking();

      if (!this.setEnabled) return;

      // ---- SET MODE ONLY ----
      // Discount tiers [{qty, amount}] sorted ascending by qty (amount = per-unit saving).
      try {
        this.tiers = JSON.parse(this.getAttribute('data-discount-tiers') || '[]')
          .filter((t) => t && t.qty)
          .sort((a, b) => a.qty - b.qty);
      } catch (e) {
        this.tiers = [];
      }
      // Best possible saving across the tiers (per-unit amount × its qty), for the "save up to £X" CTA.
      this.maxSavingCents = this.tiers.reduce((m, t) => Math.max(m, Math.round(t.amount * 100 * t.qty)), 0);

      // Per-color metadata, keyed by handle.
      this.colors = new Map();
      this.panes.forEach((p) => {
        const handle = p.getAttribute('data-color-handle');
        this.colors.set(handle, {
          handle,
          name: p.getAttribute('data-color-name') || handle,
          variantId: Number(p.getAttribute('data-variant-id')),
          unitPrice: parseInt(p.getAttribute('data-unit-price'), 10) || 0,
          available: p.getAttribute('data-available') === 'true',
        });
      });

      // Pre-select the main product if it's available.
      const main = this.colors.get(this.mainHandle);
      if (main && main.available) this.selected.add(this.mainHandle);

      // Cache shared set-summary nodes.
      this.summary = this.querySelector('[data-set-summary]');
      this.countEl = this.querySelector('[data-set-count]');
      this.addButton = this.querySelector('[data-set-add]');

      // Place the set bar in the info column, just after the swatches of the active pane (desktop:
      // sits in fp__info; mobile: in the flow after the swatches, not floating over the image).
      this.relocateSet(this.currentPane());

      // Initial paint.
      this.updateToggle(this.currentPane());
      this.updateSwatchMarkers();
      this.renderSet();
    }

    onClick(event) {
      // Preview swap (swatch).
      const swatch = event.target.closest('[data-color-swatch]');
      if (swatch && this.contains(swatch)) {
        event.preventDefault();
        const handle = swatch.getAttribute('data-color-handle');
        if (handle) this.activate(handle);
        return;
      }
      // Add/remove the previewed color to/from the set.
      const toggle = event.target.closest('[data-set-toggle]');
      if (toggle && this.contains(toggle)) {
        this.toggleSelect(toggle.getAttribute('data-color-handle'));
        return;
      }
      // Commit the whole set.
      const add = event.target.closest('[data-set-add]');
      if (add && this.contains(add) && !add.hasAttribute('disabled')) {
        this.addToCart();
      }
    }

    // ---- Preview ----------------------------------------------------------
    activate(handle) {
      const target = this.panes.find((p) => p.getAttribute('data-color-handle') === handle);
      if (!target || target === this.currentPane()) return;
      this.panes.forEach((pane) => {
        const isTarget = pane === target;
        pane.toggleAttribute('hidden', !isTarget);
        pane.classList.toggle('is-active', isTarget);
      });
      this.setAttribute('data-active-handle', handle);
      this.trackSelect(handle);
      if (!this.setEnabled) return;
      this.updateToggle(target);
      this.relocateSet(target);
      // When nothing is explicitly selected, the button/nudge reflect the previewed color — so
      // re-render on preview switch.
      if (!this.selected.size) this.renderSet();
    }

    currentPane() {
      return this.panes.find((p) => p.classList.contains('is-active')) ||
             this.panes.find((p) => !p.hasAttribute('hidden'));
    }

    // ---- Selection --------------------------------------------------------
    toggleSelect(handle) {
      const color = this.colors.get(handle);
      if (!color || !color.available) return;
      if (this.selected.has(handle)) this.selected.delete(handle);
      else this.selected.add(handle);
      this.afterSelectionChange();
    }

    afterSelectionChange() {
      this.updateToggle(this.currentPane());
      this.updateSwatchMarkers();
      this.renderSet();
    }

    // The "Add to set" CTA also carries the save-a-set upsell (concise, no product name).
    updateToggle(pane) {
      if (!pane) return;
      const handle = pane.getAttribute('data-color-handle');
      const color = this.colors.get(handle);
      const btn = pane.querySelector('[data-set-toggle]');
      if (!btn || !color) return;
      if (!color.available) {
        btn.disabled = true;
        btn.textContent = 'Sold out';
        return;
      }
      btn.disabled = false;
      const inSet = this.selected.has(handle);
      btn.setAttribute('aria-pressed', inSet ? 'true' : 'false');
      if (inSet) {
        btn.textContent = '✓ In your set — remove';
      } else if (this.maxSavingCents > 0) {
        btn.textContent = `Add to set & save up to ${formatMoney(this.maxSavingCents, this.moneyFormat)}`;
      } else {
        btn.textContent = 'Add to set';
      }
    }

    // Mark every swatch (across all panes) that's currently in the set.
    updateSwatchMarkers() {
      this.querySelectorAll('[data-color-swatch]').forEach((sw) => {
        sw.classList.toggle('is-in-set', this.selected.has(sw.getAttribute('data-color-handle')));
      });
    }

    // ---- Discount math + summary -----------------------------------------
    // Highest tier whose qty threshold is met by the current count.
    activeTier(count) {
      let tier = null;
      for (const t of this.tiers) if (count >= t.qty) tier = t;
      return tier;
    }
    nextTier(count) {
      return this.tiers.find((t) => t.qty > count) || null;
    }

    // What "Add to cart" will commit: the explicitly-selected colors, or — when nothing is
    // selected — the color currently being previewed (so you can add what you're viewing without
    // first toggling it into the set, and the button is never blocked).
    orderHandles() {
      if (this.selected.size) return Array.from(this.selected);
      const active = this.getAttribute('data-active-handle');
      const color = this.colors.get(active);
      return color && color.available ? [active] : [];
    }

    renderSet() {
      const order = this.orderHandles();
      const count = order.length;

      // Totals + discount (amount = per-unit saving at the reached tier; total = amount × count).
      const subtotal = order.reduce((acc, h) => acc + (this.colors.get(h)?.unitPrice || 0), 0);
      const tier = this.activeTier(count);
      const perUnit = tier ? tier.amount : 0; // major units
      const savingCents = Math.round(perUnit * 100 * count);
      const discounted = subtotal - savingCents;
      const fmt = (c) => formatMoney(c, this.moneyFormat);

      // Commit button — "Add to cart" + subtotal (struck when discounted) + discounted price.
      // Disabled only when there's genuinely nothing to add (previewed color sold out, none selected).
      if (count === 0) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Sold out';
      } else {
        this.addButton.disabled = false;
        this.addButton.innerHTML = savingCents > 0
          ? `Add to cart <span class="fp__price-was">${fmt(subtotal)}</span> <span class="fp__price-now">${fmt(discounted)}</span>`
          : `Add to cart <span class="fp__price-now">${fmt(subtotal)}</span>`;
      }
    }

    // ---- Add to cart (one request) + open drawer -------------------------
    async addToCart() {
      const items = this.orderHandles()
        .map((h) => this.colors.get(h))
        .filter((s) => s && s.variantId)
        .map((s) => ({ id: s.variantId, quantity: 1 }));
      if (!items.length) return;

      const root = cartRoot();
      const tv = window.themeVariables;
      const drawer = document.querySelector('cart-drawer');
      const section = drawer && drawer.closest('.shopify-section');
      const sectionId = section ? section.id.replace('shopify-section-', '') : null;

      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
      try {
        const body = { items };
        if (sectionId) {
          body.sections = sectionId;
          body.sections_url = window.location.pathname;
        }
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const addJson = await res.json();
        if (!res.ok) {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = false;
          return;
        }
        // Cart-as-page themes: go to the cart page like the standard product form does.
        if (tv && tv.settings && (tv.settings.cartType === 'page' || tv.settings.pageType === 'cart')) {
          window.location.href = `${root}cart`;
          return;
        }
        const cart = await (await fetch(`${root}cart.js`)).json();
        if (addJson.sections) cart.sections = addJson.sections;
        // Reuse the theme's own event so the cart drawer refreshes and opens.
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart },
        }));
      } catch (e) {
        // Surface nothing fancy in the concept build; leave the button usable.
      } finally {
        this.addButton.removeAttribute('aria-busy');
        this.renderSet();
      }
    }

    // ---- Analytics (provider-agnostic via window.ZoevaAnalytics) ---------
    // The controller only decides WHAT happened and WHEN; the provider opinion lives in analytics.js.
    initTracking() {
      // The [data-fp-tracking] payload is only emitted on pages allowlisted in
      // snippets/elevar-tracking-enabled — elsewhere this bails here and trackSelect() no-ops.
      const el = this.querySelector('[data-fp-tracking]');
      if (!el || !window.ZoevaAnalytics) return;
      try {
        this.tracking = JSON.parse(el.textContent);
      } catch (e) {
        this.tracking = null;
      }
      if (!this.tracking || !Array.isArray(this.tracking.products) || !this.tracking.products.length) {
        this.tracking = null;
        return;
      }
      this.trackingByHandle = {};
      this.tracking.products.forEach((p) => { this.trackingByHandle[p.handle] = p; });

      // Fire impressions + the active product's view only once the section actually scrolls into view.
      const fire = () => {
        if (this.trackingViewFired) return;
        this.trackingViewFired = true;
        const t = this.tracking;
        window.ZoevaAnalytics.productImpressions({ products: t.products, list: t.list, currency: t.currency });
        // Guard the auto product-view to once per page — multiple sections shouldn't each report a
        // "detail view" on load. User-driven swatch views (trackSelect) still fire per section.
        if (!window.__zoevaFpViewFired) {
          window.__zoevaFpViewFired = true;
          const active = this.trackingByHandle[this.getAttribute('data-active-handle')];
          if (active) window.ZoevaAnalytics.productView({ product: active, list: t.list, currency: t.currency });
        }
      };
      if (!('IntersectionObserver' in window)) { fire(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); fire(); }
      }, { threshold: 0.25 });
      io.observe(this);
    }

    // A shopper clicked a swatch to view another colour: select + view (genuine, fires per section).
    trackSelect(handle) {
      if (!this.tracking || !window.ZoevaAnalytics) return;
      const product = this.trackingByHandle[handle];
      if (!product) return;
      const t = this.tracking;
      window.__zoevaFpViewFired = true; // an explicit view supersedes the on-load auto-view guard
      window.ZoevaAnalytics.productSelect({ product, list: t.list, currency: t.currency });
      window.ZoevaAnalytics.productView({ product, list: t.list, currency: t.currency });
    }

    // ---- Layout / preload helpers ----------------------------------------
    // Non-set mode: move a pane's standard buy-buttons block to just after its swatch block.
    relocateBuyButtons(pane) {
      const swatches = pane.querySelector('.fp-swatches');
      const buy = pane.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
      if (swatches && buy && swatches.parentElement) {
        swatches.insertAdjacentElement('afterend', buy);
      }
    }

    // Move the shared set bar into the given pane's info column, just after the swatch block.
    relocateSet(pane) {
      if (!this.summary || !pane) return;
      const swatches = pane.querySelector('.fp-swatches');
      if (swatches && swatches.parentElement) {
        swatches.insertAdjacentElement('afterend', this.summary);
      } else {
        const info = pane.querySelector('.fp__info');
        if (info) info.appendChild(this.summary);
      }
    }

    relocateSwatches(pane) {
      const swatches = pane.querySelector('.fp-swatches');
      const list = pane.querySelector('.product-info__block-list');
      if (!swatches || !list || swatches.parentElement === list) return;
      const anchor =
        list.querySelector('[data-block-type="payment-terms"]') ||
        list.querySelector('[data-block-type="description"]') ||
        list.querySelector('[data-block-type="separator"]');
      if (anchor) list.insertBefore(swatches, anchor);
      else list.appendChild(swatches);
    }

    setupImagePreload() {
      if (this.dataset.preloaded) return;
      if (!('IntersectionObserver' in window)) {
        this.preloadImages();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            this.preloadImages();
          }
        },
        { rootMargin: '400px 0px' }
      );
      io.observe(this);
    }

    preloadImages() {
      if (this.dataset.preloaded) return;
      this.dataset.preloaded = 'true';
      const run = () => {
        this.querySelectorAll('img').forEach((img) => {
          if (img.complete && img.naturalWidth > 0) return;
          if (img.loading !== 'eager') img.loading = 'eager';
        });
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 0);
    }
  }

  window.customElements.define('featured-products-picker', FeaturedProductsPicker);
})();
